import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Scan, Check, Package, Trash2, MoveRight, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import * as greigeStorageApi from '@/services/greigeStorageApi';
import * as validationService from '@/services/validationService';

const SortNoTransfer = () => {
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState('');
  const [staffVerified, setStaffVerified] = useState(false);
  const [staffName, setStaffName] = useState('');
  
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [scannedBundles, setScannedBundles] = useState([]);
  const [newSortNo, setNewSortNo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [relocations, setRelocations] = useState([]);
  const [relocDest, setRelocDest] = useState({});
  const [relocBusy, setRelocBusy] = useState(null);
  const barcodeRef = useRef(null);

  const handleVerifyStaff = async () => {
    if (!staffId.trim()) {
      toast.error('Please enter Staff ID');
      return;
    }
    try {
      const result = await validationService.quickValidateStaff(staffId.trim());
      if (result.valid) {
        setStaffVerified(true);
        setStaffName(result.staffName);
        toast.success(`Staff verified: ${result.staffName}`);
        setTimeout(() => barcodeRef.current?.focus(), 100);
      } else {
        toast.error(result.message || 'Staff not found');
      }
    } catch (error) {
      toast.error('Staff not found');
    }
  };

  const handleScanBundle = (e) => {
    if (e.key !== 'Enter' || !bundleBarcode.trim()) return;
    
    if (scannedBundles.some(b => b.barcode === bundleBarcode.trim())) {
      toast.error('Bundle already scanned');
      setBundleBarcode('');
      return;
    }
    
    // Add bundle with placeholder sort_no (will be fetched from backend)
    setScannedBundles(prev => [{
      barcode: bundleBarcode.trim(),
      current_sort_no: 'Loading...'
    }, ...prev]);
    
    toast.success('Bundle added');
    setBundleBarcode('');
    barcodeRef.current?.focus();
  };

  const handleRemoveBundle = (barcode) => {
    setScannedBundles(prev => prev.filter(b => b.barcode !== barcode));
  };

  const handleSubmit = async () => {
    if (!newSortNo.trim()) {
      toast.error('Please enter new Sort No');
      return;
    }
    if (scannedBundles.length === 0) {
      toast.error('Please scan at least one bundle');
      return;
    }
    
    try {
      setSubmitting(true);
      const result = await greigeStorageApi.sortNoTransfer(
        staffId,
        scannedBundles.map(b => b.barcode),
        newSortNo.trim()
      );
      
      toast.success(`Transferred ${result.transferred?.length || 0} bundles to Sort No ${newSortNo}`);
      
      if (result.errors?.length > 0) {
        result.errors.forEach(err => toast.warning(err));
      }
      
      setScannedBundles([]);
      setNewSortNo('');

      // PHASE 4 — design-changing transfers must be physically relocated now.
      if (result.relocations?.length > 0) {
        setRelocations(result.relocations);
        const dest = {};
        result.relocations.forEach((r) => { dest[r.bundle_barcode] = r.suggested_location_id || ''; });
        setRelocDest(dest);
        toast.warning(`${result.relocations.length} bundle(s) changed design — confirm the move to the new design's location`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmReloc = async (r) => {
    const dest = (relocDest[r.bundle_barcode] || '').trim();
    if (!dest) { toast.error('Enter/confirm a destination location'); return; }
    setRelocBusy(r.bundle_barcode);
    try {
      const res = await greigeStorageApi.confirmRelocation(r.bundle_barcode, dest, staffId);
      toast.success(res.message || 'Bundle relocated');
      setRelocations((prev) => prev.filter((x) => x.bundle_barcode !== r.bundle_barcode));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Relocation failed');
    } finally {
      setRelocBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/sections/greige-storage')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="sort-transfer-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Sort No Transfer</h1>
              <p className="text-sm text-[#E8DCC8]">Greige Storage</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        {/* Staff Verification */}
        {!staffVerified ? (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle>Staff Verification</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="staffId">Staff ID</Label>
                  <Input
                    id="staffId"
                    value={staffId}
                    onChange={(e) => setStaffId(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleVerifyStaff()}
                    placeholder="Scan or enter Staff ID..."
                    className="mt-1"
                    data-testid="sort-transfer-staff-input"
                  />
                </div>
                <Button onClick={handleVerifyStaff} className="w-full bg-[#2D5F4F] hover:bg-[#1A1F2E]" data-testid="sort-transfer-verify-btn">
                  Verify Staff
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Staff Info */}
            <Card className="border-[#E5DFD0] mb-6">
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-[#5A5F6E]">Staff</p>
                      <p className="font-medium text-[#1A1F2E]">{staffName} ({staffId})</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* New Sort No */}
            <Card className="border-[#E5DFD0] mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5" />
                  New Sort No
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  value={newSortNo}
                  onChange={(e) => setNewSortNo(e.target.value)}
                  placeholder="Enter new sort number..."
                  data-testid="new-sort-no-input"
                />
                <p className="text-xs text-[#5A5F6E] mt-2">
                  All scanned bundles will be transferred to this sort number
                </p>
              </CardContent>
            </Card>

            {/* Bundle Scanning */}
            <Card className="border-[#E5DFD0] mb-6">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Scan className="w-5 h-5" />
                    Scan Bundles
                  </span>
                  <Badge>{scannedBundles.length} Scanned</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  ref={barcodeRef}
                  value={bundleBarcode}
                  onChange={(e) => setBundleBarcode(e.target.value)}
                  onKeyDown={handleScanBundle}
                  placeholder="Scan bundle barcode..."
                  data-testid="bundle-barcode-input"
                />
                
                {scannedBundles.length > 0 && (
                  <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
                    {scannedBundles.map((bundle, idx) => (
                      <div key={bundle.barcode} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">{idx + 1}</span>
                          <Package className="w-4 h-4 text-gray-400" />
                          <span className="font-mono text-sm">{bundle.barcode}</span>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleRemoveBundle(bundle.barcode)}
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Submit */}
            <Button 
              onClick={handleSubmit}
              disabled={submitting || !newSortNo.trim() || scannedBundles.length === 0}
              className="w-full bg-[#2D5F4F] hover:bg-[#1A1F2E]"
              data-testid="submit-transfer-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {submitting ? 'Transferring...' : `Transfer ${scannedBundles.length} Bundles to Sort No ${newSortNo || '...'}`}
            </Button>
          </>
        )}
      </main>

      {/* PHASE 4 — mandatory relocation of design-changed bundles */}
      <Dialog open={relocations.length > 0} onOpenChange={() => { /* blocking — must confirm each */ }}>
        <DialogContent className="max-w-lg" data-testid="relocation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MoveRight className="w-5 h-5 text-[#2D5F4F]" /> Move to new design's location
            </DialogTitle>
            <DialogDescription>
              These bundles changed design and must be physically moved. Their aging clock is preserved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[55vh] overflow-y-auto">
            {relocations.map((r) => (
              <div key={r.bundle_barcode} className="rounded-lg border border-[#E5DFD0] p-3 space-y-2" data-testid={`relocation-${r.bundle_barcode}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm">{r.bundle_barcode}</span>
                  <Badge className="bg-indigo-100 text-indigo-700 border-0 text-[10px]">{r.old_design} → {r.new_design}</Badge>
                </div>
                <p className="text-xs text-[#5A5F6E] flex items-center gap-1"><MapPin className="w-3 h-3" /> Now at: {r.current_footprint || r.current_location_id}</p>
                <div>
                  <Label className="text-xs text-[#5A5F6E]">Suggested: {r.suggested_footprint || r.suggested_location_id || 'no suggestion — scan a location'}</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={relocDest[r.bundle_barcode] || ''}
                      onChange={(e) => setRelocDest((p) => ({ ...p, [r.bundle_barcode]: e.target.value }))}
                      placeholder="Destination location ID"
                      className="flex-1 h-9"
                      data-testid={`relocation-dest-${r.bundle_barcode}`}
                    />
                    <Button
                      onClick={() => handleConfirmReloc(r)}
                      disabled={relocBusy === r.bundle_barcode}
                      className="bg-[#2D5F4F] hover:bg-[#1A1F2E] h-9"
                      data-testid={`relocation-confirm-${r.bundle_barcode}`}
                    >
                      {relocBusy === r.bundle_barcode ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Confirm Move'}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SortNoTransfer;
